import React, { useState, useEffect, useCallback } from 'react';
import GeneralTab from './GeneralTab';
import TemplatesTab from './TemplatesTab';
import BonusesTab from './BonusesTab';
import PromosTab from './PromosTab';
import AnalyticsTab from './AnalyticsTab';
import BroadcastTab from './BroadcastTab';
import AdminNewsBanner from './AdminNewsBanner';
import './Admin.css';
import '../../telegram/cards.css';
import { resolveTemplateColors } from '../../utils/templateColors';

const TABS = [
  { id: 'general', label: 'Общие' },
  { id: 'templates', label: 'Шаблоны' },
  { id: 'bonuses', label: 'Бонусы' },
  { id: 'promos', label: 'Акции' },
  { id: 'broadcast', label: 'Рассылки' },
  { id: 'analytics', label: 'Аналитика' },
];

const NO_SAVE_TABS = ['analytics', 'broadcast'];

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [user, setUser] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [params, setParams] = useState({});

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setParams({
      contextKey: urlParams.get('contextKey') || '',
      appUid: urlParams.get('appUid') || '',
      appId: urlParams.get('appId') || '',
    });
    document.documentElement.classList.add('admin-mode');
    document.body.classList.add('admin-mode');
    const root = document.getElementById('root');
    root?.classList.add('admin-root');
    return () => {
      document.documentElement.classList.remove('admin-mode');
      document.body.classList.remove('admin-mode');
      root?.classList.remove('admin-root');
    };
  }, []);

  const loadSettings = useCallback(async () => {
    if (!params.contextKey || !params.appUid) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ contextKey: params.contextKey, appUid: params.appUid });
      const data = await (await fetch(`/admin/settings?${qs}`)).json();
      setUser(data.user);
      setTemplates((data.templates || []).map((t) => {
        const colors = resolveTemplateColors(t);
        return {
          ...t,
          mainColor: colors.mainColor,
          textColor: colors.textColor,
          labelColor: colors.labelColor,
          backgroundPattern: t.backgroundPattern || 'solid',
        };
      }));
      setPromos(data.promos || []);
    } catch {
      setMessage('Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [params.contextKey, params.appUid]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const saveSettings = async (templatesOverride) => {
    setSaving(true);
    setMessage('');
    try {
      const res = await fetch('/admin/settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accountId: user.accountId,
          appId: params.appId,
          appUid: params.appUid,
          userToken: user.token,
          userName: user.accountName,
          isIssueCards: user.isIssueCards,
          needVerification: user.needVerification,
          logoUrl: user.logoUrl,
          backgroundUrl: user.backgroundUrl,
          mainColor: user.mainColor,
          textColor: user.textColor,
          labelColor: user.labelColor,
          backText: user.backText,
          welcomeBonusEnabled: user.welcomeBonusEnabled,
          welcomeBonusAmount: user.welcomeBonusAmount,
          birthdayBonusEnabled: user.birthdayBonusEnabled,
          birthdayBonusAmount: user.birthdayBonusAmount,
          birthdayBonusDaysBefore: user.birthdayBonusDaysBefore,
          collectBirthDate: user.collectBirthDate,
          referralEnabled: user.referralEnabled,
          referralBonusReferrer: user.referralBonusReferrer,
          referralBonusReferred: user.referralBonusReferred,
          templates: templatesOverride ?? templates,
          promos,
        }),
      });
      const data = await res.json();
      setMessage(data.message || 'Сохранено');
      return res.ok;
    } catch {
      setMessage('Ошибка сохранения');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => saveSettings();

  const handleAddTemplate = async () => {
    const qs = new URLSearchParams({ contextKey: params.contextKey, appUid: params.appUid });
    const newTemplate = await (await fetch(`/admin/templates?${qs}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: `Уровень ${templates.length + 1}`, accountId: user.accountId }),
    })).json();
    setTemplates([...templates, newTemplate]);
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Удалить шаблон?')) return;
    const qs = new URLSearchParams({ contextKey: params.contextKey, appUid: params.appUid, accountId: user.accountId });
    const res = await fetch(`/admin/templates/${id}?${qs}`, { method: 'DELETE' });
    const data = await res.json();
    if (res.ok) setTemplates(templates.filter((t) => t._id !== id));
    else alert(data.message || 'Не удалось удалить');
  };

  const handleReorder = async (orderedIds) => {
    const qs = new URLSearchParams({ contextKey: params.contextKey, appUid: params.appUid });
    try {
      const res = await fetch(`/admin/templates/reorder?${qs}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderedIds, accountId: user.accountId }),
      });
      const data = await res.json();
      if (!res.ok || !Array.isArray(data)) {
        alert(data.message || 'Не удалось изменить порядок шаблонов');
        await loadSettings();
        return;
      }
      setTemplates((data || []).map((t) => {
        const colors = resolveTemplateColors(t);
        return {
          ...t,
          mainColor: colors.mainColor,
          textColor: colors.textColor,
          labelColor: colors.labelColor,
          backgroundPattern: t.backgroundPattern || 'solid',
        };
      }));
    } catch {
      alert('Ошибка при изменении порядка шаблонов');
      await loadSettings();
    }
  };

  const updateTemplate = (index, field, value) => {
    setTemplates((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const updateUser = (field, value) => {
    setUser((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const uploadImage = async (endpoint, file) => {
    const formData = new FormData();
    formData.append('image', file);
    const res = await fetch(endpoint, { method: 'POST', body: formData });
    let data = {};
    try {
      data = await res.json();
    } catch {
      data = {};
    }
    if (!res.ok) {
      const msg = Array.isArray(data.message)
        ? data.message.join(', ')
        : (data.message || 'Не удалось загрузить файл');
      throw new Error(msg);
    }
    if (!data.url) {
      throw new Error('Сервер не вернул адрес файла');
    }
    return data.url;
  };

  if (loading) return <div className="admin-panel"><p className="admin-loading">Загрузка...</p></div>;
  if (!user) return <div className="admin-panel"><p className="admin-error">Откройте кабинет из МойСклад</p></div>;

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <div>
          <h1>Лояльность</h1>
          <p className="admin-subtitle">{user.accountName}</p>
        </div>
      </header>

      <nav className="admin-tabs">
        {TABS.map((tab) => (
          <button key={tab.id} type="button" className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
            {tab.label}
          </button>
        ))}
      </nav>

      <AdminNewsBanner />

      <main className="admin-content">
        {activeTab === 'general' && <GeneralTab user={user} updateUser={updateUser} uploadImage={uploadImage} />}
        {activeTab === 'templates' && (
          <TemplatesTab templates={templates} user={user} updateTemplate={updateTemplate}
            onAdd={handleAddTemplate} onDelete={handleDeleteTemplate} />
        )}
        {activeTab === 'bonuses' && <BonusesTab user={user} updateUser={updateUser} />}
        {activeTab === 'promos' && <PromosTab promos={promos} templates={templates} updatePromos={setPromos} />}
        {activeTab === 'broadcast' && (
          <BroadcastTab contextKey={params.contextKey} appUid={params.appUid} templates={templates} user={user} />
        )}
        {activeTab === 'analytics' && (
          <AnalyticsTab contextKey={params.contextKey} appUid={params.appUid} accountId={user?.accountId} />
        )}
      </main>

      {!NO_SAVE_TABS.includes(activeTab) && (
        <footer className="admin-footer">
          {message && <span className="admin-message">{message}</span>}
          <button type="button" className="admin-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? '...' : 'Сохранить'}
          </button>
        </footer>
      )}
    </div>
  );
};

export default AdminPanel;
